import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, RotateCcw, ChevronDown } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { HEADER } from '@/constants/testIds';

const LEARNER_KEYS = ['learnerId', 'studentName'];
const DEVICE_KEYS = [...LEARNER_KEYS, 'courseLanguage'];

export const UserMenu = ({ studentName }) => {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(null); // 'logoff' | 'reset'

  const signOut = (keys) => {
    keys.forEach(k => localStorage.removeItem(k));
    sessionStorage.removeItem('studentName');
    setConfirm(null);
    navigate('/', { replace: true });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="user-menu-trigger"
            className="flex items-center gap-1 text-sm font-body min-w-0 rounded-md px-2 py-1 hover:bg-white/5 transition-colors"
          >
            <span data-testid={HEADER.studentName} className="text-[#FFB800] font-medium truncate max-w-[80px] sm:max-w-[180px]">{studentName}</span>
            <ChevronDown className="w-3.5 h-3.5 text-[#71717A] flex-shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#0F0F12] border-white/10 text-white w-56">
          <DropdownMenuLabel className="text-[#71717A] text-xs font-code">Signed in as {studentName}</DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-white/10" />
          <DropdownMenuItem data-testid="user-menu-logoff" onSelect={() => setConfirm('logoff')} className="cursor-pointer focus:bg-white/5 focus:text-white">
            <LogOut className="w-4 h-4 mr-2" /> Log off
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="user-menu-reset" onSelect={() => setConfirm('reset')} className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-400">
            <RotateCcw className="w-4 h-4 mr-2" /> Reset this device
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="user-menu-confirm-dialog" className="bg-[#0F0F12] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === 'reset' ? 'Reset this device?' : 'Log off?'}</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              {confirm === 'reset'
                ? 'This forgets your name and course choice on this device and starts fresh. Your saved progress is not deleted.'
                : 'You will return to the sign-in screen. Enter the same name to continue where you left off.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-menu-cancel" className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="user-menu-confirm"
              onClick={() => signOut(confirm === 'reset' ? DEVICE_KEYS : LEARNER_KEYS)}
              className={confirm === 'reset' ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-[#FFB800] hover:bg-[#FFC820] text-black'}
            >
              {confirm === 'reset' ? 'Reset' : 'Log off'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
